/**
 * Tests Page
 *
 * Unified test management page with 4 tabs:
 * - Configurations: Saved test configurations (default)
 * - Upcoming: Scheduled tests waiting to run
 * - Running: Tests currently executing
 * - History: Completed/failed/cancelled tests
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Modal, EmptyState, DeleteConfirmDialog,
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui';
import { Column } from '@/components/ui/data-table';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  PlayCircle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RotateCw,
  StopCircle,
  Download,
  Activity,
  Terminal,
  Calendar,
  Loader2,
  FileText,
  Settings,
  Copy,
  Trash2,
  Edit,
  Layers,
  History,
  Repeat,
  CalendarClock,
  Zap,
  Phone,
  AlertTriangle,
  Pause,
  BookmarkPlus,
  FlaskConical,
} from 'lucide-react';
import { Switch } from '@/components/ui/Switch';
import { cn, formatDuration, formatRelativeTime } from '@/lib/utils';
import { ExecutionArtifacts } from '@/components/artifacts/ExecutionArtifacts';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { ESIM_MOVE_DECLINED, isEsimMoveApprovalError, useEsimMoveApproval } from '@/components/esim/EsimMoveApproval';
import { CampaignRunDetail } from '@/components/campaigns/CampaignRunDetail';
import { SelectOrganizationButton } from '@/components/admin/SelectOrganizationButton';
import { ExecutionDialog } from '@/components/execution-wizard';
import {
  getTestConfigurations,
  getTestRunStats,
  deleteTestConfiguration,
  duplicateTestConfiguration,
  saveRunAsConfiguration,
  updateTestConfiguration,
  dispatchRunFromConfig,
  cancelTestRun,
  deleteTestRun,
  subscribeToTestRuns,
  subscribeToTestRun,
  subscribeToUpcomingTestRuns,
} from '@/services/testConfigurationService';
import { ExecutionLogViewer } from '@/components/execution/ExecutionLogViewer';
import { ExecutionProgress } from '@/components/execution/ExecutionProgress';
import { useExecutionLogs } from '@/hooks/useExecutionLogs';
import { artifactKindLabel, artifactSkipReason, visibleArtifactSkips } from '@/utils/artifactSkips';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useUserNames } from '@/hooks/useUserNames';
import { ConnectivitySnapshot } from '@/components/execution-analyzer/ConnectivitySnapshot';
import { NetworkDiagnosticsPanel } from '@/components/execution-analyzer/NetworkDiagnosticsPanel';
import { SpeedTestGauges } from '@/components/quick-tests/SpeedTestGauges';
import { TestRunSetup } from '@/components/tests/TestRunSetup';
import { quickTestName } from '@/utils/quickTestNames';
import { firstStepTime, wholeRunMs } from '@/utils/runDuration';
import { fetchArtifactSignedUrl } from '@/services/reportsService';
import type { TestConfiguration, TestRun } from '@simcheck/shared-types';

type TabValue = 'configurations' | 'upcoming' | 'running' | 'history';

/**
 * Badge shown next to a run's flow name when it was triggered by a schedule
 * rather than run on-demand — so the customer can tell scheduler-driven runs
 * apart in Running/History. 'recurring' = fired by a recurring schedule;
 * 'scheduled' = a one-off scheduled run. Manual runs render nothing.
 */
/**
 * The test run id (shown as "ID:" in Test Run Details) with a copy button,
 * so support can quote a run without opening it.
 */
function TaskIdCell({ id }: { id: string }) {
  const { success, error: showError } = useToast();
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      success('Task ID copied');
    } catch {
      showError('Could not copy the Task ID');
    }
  };
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className="font-mono text-xs text-muted-foreground truncate" title={id}>{id}</span>
      <Button
        variant="ghost"
        size="sm"
        icon={<Copy className="w-3.5 h-3.5" />}
        onClick={copy}
        title="Copy Task ID"
        aria-label="Copy Task ID"
      />
    </div>
  );
}

/**
 * Where a run came from, from fields the person can't edit (names can be):
 * a schedule or trigger (triggerSource), else Quick Tests (its system
 * configuration, `sys_config_*`), else a saved configuration — including one
 * made with History → Save as configuration — else a one-off from Run Test
 * (no configuration id, or the snapshot's 'inline').
 * Retries carry no testConfigurationId, so the snapshot's id is the fallback.
 */
const RUN_SOURCE = {
  recurring: { icon: Repeat, label: 'Recurring', title: 'Run by a recurring schedule of a configuration' },
  scheduled: { icon: CalendarClock, label: 'Scheduled', title: 'Run by a one-off schedule of a configuration' },
  event: { icon: Zap, label: 'Triggered', title: 'Run by an event trigger' },
  quick: { icon: FlaskConical, label: 'Quick Test', title: 'Started from the Quick Tests page' },
  config: { icon: Settings, label: 'Configuration', title: 'Started by hand from a saved configuration' },
  oneOff: { icon: PlayCircle, label: 'One-off', title: 'Started from Run Test without saving a configuration' },
} as const;

function runSource(run: TestRun): keyof typeof RUN_SOURCE {
  const src = (run as any).triggerSource as string | undefined;
  if (src === 'recurring') return 'recurring';
  if (src === 'event') return 'event';
  if (src === 'scheduled' || run.scheduledFor) return 'scheduled';
  const configId = run.testConfigurationId || run.configuration?.id;
  if (configId?.startsWith('sys_config_')) return 'quick';
  // Run Test without saving snapshots its configuration as id 'inline'.
  return configId && configId !== 'inline' ? 'config' : 'oneOff';
}

/**
 * History → Save as configuration: completed runs only, and not a connectivity
 * campaign (#283), which isn't a flow: a configuration saved from it would
 * lose its campaign and fail when run.
 */
function canSaveAsConfiguration(run: TestRun): boolean {
  return run.status === 'completed' && (run as any).kind !== 'connectivity_campaign';
}

function ScheduleBadge({ run }: { run: TestRun }) {
  const { icon: Icon, label, title } = RUN_SOURCE[runSource(run)];
  return (
    <Badge variant="outline" className="gap-1 text-xs font-normal shrink-0" title={title}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

/**
 * Row model for the Upcoming tab. Two sources merge here:
 * - 'run': a real one-off scheduled test_run doc (status 'initializing' with a
 *   future scheduledFor).
 * - 'recurring': a projection of an active recurring configuration's next
 *   occurrence. Recurring schedules have NO test_run doc until the
 *   processTestSchedules cron materializes one at fire time, so the config's
 *   nextFireAt is the only preview of them — without this projection the tab
 *   is empty for every recurring schedule (issue #218).
 */
interface UpcomingRow {
  id: string;
  kind: 'run' | 'recurring';
  flowName: string;
  scheduledFor: Date | null;
  configurationName: string;
  cadence?: string;
  run?: TestRun;
  config?: TestConfiguration;
}

/** "Every day" / "Every 2 hours" from a recurring config's recurrence. */
function formatCadence(config: TestConfiguration): string {
  const r = config.timing?.recurrence;
  if (!r?.interval || !r?.unit) return 'Recurring';
  const unit = String(r.unit).replace(/s$/, '');
  return r.interval === 1 ? `Every ${unit}` : `Every ${r.interval} ${unit}s`;
}

/**
 * Summary view: parse the End-node summary string into sections and render each
 * as a labeled block. A section starts with a non-indented title line and
 * contains following indented ("  ") body lines. Blank lines end a section.
 * If no structure is detected, the full text renders as a single section.
 */
function SummaryView({ text }: { text: string }) {
  const sections: Array<{ title: string | null; body: string[] }> = [];
  let current: { title: string | null; body: string[] } | null = null;

  for (const raw of text.split('\n')) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) {
      if (current) {
        sections.push(current);
        current = null;
      }
      continue;
    }
    const indented = /^\s+/.test(line);
    if (!indented) {
      if (current) sections.push(current);
      current = { title: line.trim(), body: [] };
    } else {
      if (!current) current = { title: null, body: [] };
      current.body.push(line.replace(/^\s+/, ''));
    }
  }
  if (current) sections.push(current);

  if (sections.length === 0) {
    return <div className="text-sm text-muted-foreground whitespace-pre-line font-mono">{text}</div>;
  }

  return (
    <div className="space-y-2">
      {sections.map((s, idx) => (
        <div key={idx} className="flex gap-3 pl-3 border-l-2 border-accent-blue/60">
          <div className="flex-1 min-w-0">
            {s.title && (
              <div className="text-sm font-semibold text-foreground mb-1">{s.title}</div>
            )}
            {s.body.length > 0 && (
              <div className="space-y-0.5 font-mono text-xs text-muted-foreground">
                {s.body.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Tests() {
  const { activeOrganization, currentUser, userRole, isSuperAdmin } = useAuth();
  const fmt = useDateFormat();
  // "Started At" cell for Running and History: start time in the viewer's
  // zone; hover adds seconds and, in History, when it finished.
  const renderStartedAt = (run: TestRun) => {
    if (!run.startedAt) {
      return (
        <span
          className="text-sm text-muted-foreground/60 italic whitespace-nowrap"
          title={run.createdAt ? `Queued ${fmt.formatIsoDateTime(run.createdAt, true)}` : undefined}
        >
          {run.status === 'queued' || run.status === 'initializing' ? 'Waiting' : 'Not started'}
        </span>
      );
    }
    return (
      <span
        className="text-sm text-muted-foreground font-mono whitespace-nowrap"
        title={`Started ${fmt.formatIsoDateTime(run.startedAt, true)}${
          run.completedAt ? ` · finished ${fmt.formatIsoDateTime(run.completedAt, true)}` : ''
        } (${fmt.timeZone})`}
      >
        {fmt.formatIsoDateTime(run.startedAt)}
      </span>
    );
  };
  const canManageStuckRuns = isSuperAdmin || userRole === 'owner' || userRole === 'admin';
  const { success, error: showError, info } = useToast();
  const { confirmMove, dialog: esimMoveDialog } = useEsimMoveApproval();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state - read from URL parameter or default to 'configurations'
  const urlTab = searchParams.get('tab') as TabValue | null;
  const validTabs: TabValue[] = ['configurations', 'upcoming', 'running', 'history'];
  const initialTab = urlTab && validTabs.includes(urlTab) ? urlTab : 'configurations';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);

  // Sync tab state with URL when URL changes (e.g., from notification click)
  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabValue | null;
    if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // State → URL: every tab change writes ?tab= back so refresh, share, and
  // back/forward keep the active tab. replace:true keeps tab clicks from
  // stacking up in browser history.
  const changeTab = useCallback((tab: TabValue) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Data state
  const [configurations, setConfigurations] = useState<TestConfiguration[]>([]);
  const [upcomingRuns, setUpcomingRuns] = useState<TestRun[]>([]);
  const [runningRuns, setRunningRuns] = useState<TestRun[]>([]);
  const [historyRuns, setHistoryRuns] = useState<TestRun[]>([]);
  // DataTable search/sort only read flat row keys, so mirror the nested
  // configuration name to the top level for the History tab's column.
  const historyRows = useMemo(
    () =>
      historyRuns.map((run) => ({
        ...run,
        configurationName: run.configuration?.name || '',
      })),
    [historyRuns]
  );
  // "Created By" (configurations) and "Run By" (running / history) names.
  const userName = useUserNames(
    activeOrganization?.id,
    [...configurations, ...runningRuns, ...historyRuns].map((r) => r.createdBy),
    isSuperAdmin,
  );
  // Active recurring configurations — the schedules whose next occurrence the
  // Upcoming tab projects. Paused / terminated schedules aren't "upcoming".
  const activeRecurringConfigs = useMemo(
    () =>
      configurations.filter(
        (c) =>
          c.isRecurring &&
          !c.paused &&
          (c.scheduleState === 'active' || c.scheduleState === undefined) &&
          c.nextFireAt
      ),
    [configurations]
  );

  // Upcoming tab rows: real one-off scheduled runs + a projected row per
  // active recurring schedule, soonest first. See UpcomingRow.
  const upcomingRows = useMemo<UpcomingRow[]>(() => {
    const rows: UpcomingRow[] = [
      ...upcomingRuns.map((run) => ({
        id: run.id,
        kind: 'run' as const,
        flowName: run.flowName,
        scheduledFor: run.scheduledFor ?? null,
        configurationName: run.configuration?.name || 'Inline',
        run,
      })),
      ...activeRecurringConfigs.map((config) => ({
        id: `recurring-${config.id}`,
        kind: 'recurring' as const,
        flowName: config.flowName || config.name,
        scheduledFor: config.nextFireAt ?? null,
        configurationName: config.name,
        cadence: formatCadence(config),
        config,
      })),
    ];
    rows.sort(
      (a, b) => (a.scheduledFor?.getTime() ?? Infinity) - (b.scheduledFor?.getTime() ?? Infinity)
    );
    return rows;
  }, [upcomingRuns, activeRecurringConfigs]);

  // Location name lookup map (id → displayName). Loaded once at mount so the
  // Running tab can show "Tel Aviv DC1" instead of "location-tel-aviv-dc1".
  const [locationNameById, setLocationNameById] = useState<Record<string, string>>({});
  const [stats, setStats] = useState({
    configurations: 0,
    upcoming: 0,
    running: 0,
    completed24h: 0,
    failed24h: 0,
  });

  // History → Save as configuration: the run being saved, and the
  // configuration it created (marked New in the Configurations list).
  const [savingRunId, setSavingRunId] = useState<string | null>(null);
  const [newConfigId, setNewConfigId] = useState<string | null>(null);

  // Loading states
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  // Delete waiting for confirmation in the DeleteConfirmDialog
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: 'config'; id: string; name: string }
    | { kind: 'run'; id: string; name: string }
    | { kind: 'stuck'; runs: TestRun[] }
    | null
  >(null);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  // Running is fed by the always-on subscription; loading until 1st snapshot.
  const [loadingRunning, setLoadingRunning] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Dialog states
  const [showExecutionDialog, setShowExecutionDialog] = useState(false);
  // #283 — connectivity campaigns: long-running tests that own a modem's mode.
  const [campaignRunId, setCampaignRunId] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<TestConfiguration | null>(null);
  const [selectedRun, setSelectedRun] = useState<TestRun | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);

  /**
   * Open the right detail surface for a run (#283).
   *
   * A campaign has no execution, so the generic logs modal can only ever say
   * "1 modem(s) released" — its events, scans, captures and on-attach runs live behind
   * `/test-runs/:id/campaign/*`. Before this, `setCampaignRunId` was called from exactly one
   * place (the sheet's `onStarted`), so the campaign view was reachable only in the browser
   * session that started it: a reload, a new tab, or coming back the next morning left the
   * whole run-scoped API unreachable. Every list, the mobile card and the `?runId=` deep link
   * route through here so that can't drift apart again.
   */
  const openRun = useCallback((run: TestRun) => {
    if ((run as any).kind === 'connectivity_campaign') {
      setCampaignRunId(run.id);
      return;
    }
    setSelectedRun(run);
    setShowLogsModal(true);
  }, []);

  // Edit configuration states
  const [editingConfig, setEditingConfig] = useState<TestConfiguration | null>(null);
  const [showEditWizard, setShowEditWizard] = useState(false);

  // Fetch execution logs for the selected run
  const { logs, isLive, isLoading: logsLoading, connectivity: selectedRunConnectivity, currentNode: selectedRunCurrentNode, status: selectedRunStatus, pending: selectedRunPending, diagSummary: selectedRunDiag, artifacts: selectedRunArtifacts, artifactSkips: selectedRunArtifactSkips, setupSteps: selectedRunSetupSteps } = useExecutionLogs(
    selectedRun?.executionId || null
  );

  const [downloadingArtifactId, setDownloadingArtifactId] = useState<string | null>(null);
  const handleArtifactDownload = useCallback(async (artifact: { id: string; filename?: string }) => {
    if (!activeOrganization?.id || !selectedRun?.executionId) return;
    setDownloadingArtifactId(artifact.id);
    try {
      const signed = await fetchArtifactSignedUrl(activeOrganization.id, selectedRun.executionId, artifact.id);
      const a = document.createElement('a');
      a.href = signed.signedUrl;
      a.download = signed.filename || artifact.filename || artifact.id;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 100);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to fetch download link');
    } finally {
      setDownloadingArtifactId(null);
    }
  }, [activeOrganization?.id, selectedRun?.executionId, showError]);

  // Active tab in the Test Run Details modal. Default to Logs for a live run
  // (you usually open a running test to watch it) and Overview otherwise.
  const [detailsTab, setDetailsTab] = useState<'overview' | 'logs' | 'signaling' | 'artifacts'>('overview');
  useEffect(() => {
    if (!showLogsModal) return;
    setDetailsTab(isLive ? 'logs' : 'overview');
    // Only re-evaluate when a different run is opened, not on every live tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogsModal, selectedRun?.id]);

  // Load configurations and stats on mount
  useEffect(() => {
    if (!activeOrganization) return;

    const loadInitialData = async () => {
      try {
        setLoadingConfigs(true);
        // Lazy import to avoid pulling location service into the critical path
        const { getLocations, filterLocationsByTenancy } = await import('@/services/hierarchicalLocationService');
        const [configsData, statsData, rawLocs] = await Promise.all([
          getTestConfigurations(activeOrganization.id),
          getTestRunStats(activeOrganization.id),
          getLocations().catch(() => [] as any[]),
        ]);
        // Tenancy (#202): never surface other orgs' private locations.
        const locs = filterLocationsByTenancy(rawLocs, activeOrganization.id, isSuperAdmin);
        setConfigurations(configsData);
        setStats({
          configurations: configsData.length,
          upcoming: statsData.scheduled,
          running: statsData.running,
          completed24h: statsData.completed24h,
          failed24h: statsData.failed24h,
        });
        // Build location-name lookup for the Running tab columns.
        const map: Record<string, string> = {};
        for (const l of locs) map[l.id] = l.displayName || l.name;
        setLocationNameById(map);
      } catch (err: any) {
        console.error('Failed to load data:', err);
        showError('Failed to load test data');
      } finally {
        setLoadingConfigs(false);
      }
    };

    loadInitialData();
  }, [activeOrganization]);

  // Tab-specific live data. Upcoming and History are Firestore subscriptions
  // mounted only while their tab is visible (unsubscribed on switch), so a
  // run finishing shows up in History without a manual page reload. Running
  // is covered by the always-on subscription below; Configurations loads at
  // mount. Spinner only when the tab has no cached rows yet — revisits keep
  // showing them while the fresh snapshot arrives.
  useEffect(() => {
    if (!activeOrganization) return;

    if (activeTab === 'upcoming') {
      // Recurring projections render from already-loaded configurations, so
      // only show the spinner when there's nothing to paint at all.
      setLoadingUpcoming(upcomingRuns.length === 0 && activeRecurringConfigs.length === 0);
      const unsubscribe = subscribeToUpcomingTestRuns(activeOrganization.id, (runs) => {
        setUpcomingRuns(runs);
        setLoadingUpcoming(false);
      });
      return () => unsubscribe();
    }

    if (activeTab === 'history') {
      setLoadingHistory(historyRuns.length === 0);
      const unsubscribe = subscribeToTestRuns(
        activeOrganization.id,
        (runs) => {
          setHistoryRuns(runs);
          setLoadingHistory(false);
        },
        { status: ['completed', 'failed', 'cancelled'], limitCount: 50 }
      );
      return () => unsubscribe();
    }
    // upcomingRuns/historyRuns intentionally omitted — only read for the
    // initial-spinner decision; subscribing on their change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeOrganization]);

  // Real-time subscription for running tests.
  // Side-effect: when the running count changes (a test started or finished)
  // we re-fetch the 24h stats so Completed/Failed cards stay live.
  useEffect(() => {
    if (!activeOrganization) return;

    let prevRunningCount = -1;

    const unsubscribe = subscribeToTestRuns(
      activeOrganization.id,
      (runs) => {
        // One-off scheduled runs sit in 'initializing' until the cron fires
        // them — they belong to the Upcoming tab, not Running.
        const nowMs = Date.now();
        const active = runs.filter(
          (r) => !(r.status === 'initializing' && r.scheduledFor && r.scheduledFor.getTime() > nowMs)
        );
        setRunningRuns(active);
        setLoadingRunning(false);
        // Update Running stat card live
        setStats((s) => ({ ...s, running: active.length }));

        // When the running set shrinks (a run completed/failed), refresh the
        // 24h aggregate so the cards reflect the change without a page reload.
        if (prevRunningCount !== -1 && active.length !== prevRunningCount) {
          getTestRunStats(activeOrganization.id)
            .then((statsData) => {
              setStats((s) => ({
                ...s,
                upcoming: statsData.scheduled,
                completed24h: statsData.completed24h,
                failed24h: statsData.failed24h,
              }));
            })
            .catch((err) => console.error('Failed to refresh stats:', err));
        }
        prevRunningCount = active.length;

        // Auto-open logs modal if runId is in URL (from Quick Test navigation)
        const runId = searchParams.get('runId');
        if (runId && !showLogsModal) {
          const targetRun = runs.find(r => r.id === runId);
          if (targetRun) {
            openRun(targetRun);
            // Clear runId from URL to prevent re-opening on tab switches
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('runId');
            setSearchParams(newParams, { replace: true });
          }
        }
      },
      { status: ['running', 'initializing'], limitCount: 50 }
    );

    return () => unsubscribe();
  }, [activeOrganization]);

  // Keep the Configurations stat card in sync with the in-memory list (covers
  // Duplicate / Delete / refresh-after-edit) without a separate subscription.
  useEffect(() => {
    setStats((s) => ({ ...s, configurations: configurations.length }));
  }, [configurations]);

  // Backstop: poll the 24h aggregates every 30s while the page is visible.
  // The running subscription already covers fast updates when runs start /
  // finish; this catches everything else (scheduled runs becoming upcoming,
  // recurring projections, deletions, status changes from other clients).
  // Configurations are re-pulled too so a recurring schedule's nextFireAt
  // advances in the Upcoming tab after the cron fires it.
  useEffect(() => {
    if (!activeOrganization) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      getTestRunStats(activeOrganization.id)
        .then((s) => setStats((prev) => ({
          ...prev,
          upcoming: s.scheduled,
          running: s.running,
          completed24h: s.completed24h,
          failed24h: s.failed24h,
        })))
        .catch((err) => console.error('Stats backstop refresh failed:', err));
      getTestConfigurations(activeOrganization.id)
        .then(setConfigurations)
        .catch((err) => console.error('Configurations backstop refresh failed:', err));
    };
    const id = window.setInterval(refresh, 30_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [activeOrganization]);

  // Real-time subscription for selected run (modal)
  // This keeps the modal updated when the test run status changes
  useEffect(() => {
    if (!selectedRun || !showLogsModal) return;

    const unsubscribe = subscribeToTestRun(selectedRun.id, (updatedRun) => {
      if (updatedRun) {
        setSelectedRun(updatedRun);
      }
    });

    return () => unsubscribe();
  }, [selectedRun?.id, showLogsModal]);

  // Action handlers
  const handleDeleteConfig = async (configId: string) => {
    if (!activeOrganization) return;
    try {
      await deleteTestConfiguration(configId, activeOrganization.id);
      setConfigurations((prev) => prev.filter((c) => c.id !== configId));
      success('Configuration deleted');
    } catch (err: any) {
      showError(err.message || 'Failed to delete configuration');
    }
  };

  const handleDuplicateConfig = async (config: TestConfiguration) => {
    if (!activeOrganization || !currentUser) return;
    try {
      const newConfig = await duplicateTestConfiguration(
        config.id,
        activeOrganization.id,
        currentUser.uid
      );
      setConfigurations((prev) => [newConfig, ...prev]);
      success('Configuration duplicated');
    } catch (err: any) {
      showError(err.message || 'Failed to duplicate configuration');
    }
  };

  const handleCancelRun = async (runId: string) => {
    if (!activeOrganization) return;
    try {
      await cancelTestRun(runId, activeOrganization.id);
      success('Test run cancelled');
    } catch (err: any) {
      showError(err.message || 'Failed to cancel test run');
    }
  };

  const handleDeleteRun = async (runId: string) => {
    if (!activeOrganization) return;
    try {
      await deleteTestRun(runId, activeOrganization.id);
      setHistoryRuns((prev) => prev.filter((r) => r.id !== runId));
      success('Test run deleted');
    } catch (err: any) {
      showError(err.message || 'Failed to delete test run');
    }
  };

  const handleRunStarted = (runId: string) => {
    changeTab('running');
    // If the user saved a new configuration as part of submission, the
    // Configurations list won't have it without a refetch. Cheap to always
    // re-pull and keeps the table + stat card in sync with Firestore.
    if (activeOrganization) {
      getTestConfigurations(activeOrganization.id)
        .then(setConfigurations)
        .catch((err) => console.error('Failed to refresh configurations after run:', err));
    }
  };

  /**
   * One-click run for a saved configuration row (no dialog).
   * Used by the row play icon + History "Retry" (with a snapshot).
   */
  const handleQuickRunConfig = async (config: TestConfiguration, savedConfigId?: string) => {
    if (!activeOrganization || !currentUser) return;
    const dispatch = (c: TestConfiguration) =>
      dispatchRunFromConfig(activeOrganization.id, currentUser.uid, c, savedConfigId);
    try {
      let runId: string;
      try {
        ({ runId } = await dispatch(config));
      } catch (err: any) {
        // #375: refused only for want of consent to move a limited-download
        // eSIM — ask, and start an approved run (the refused one stays in history).
        if (!isEsimMoveApprovalError(err)) throw err;
        if (!(await confirmMove(err.message))) {
          info(ESIM_MOVE_DECLINED);
          return;
        }
        ({ runId } = await dispatch({ ...config, esim: { ...config.esim, allowRelocation: true } }));
      }
      success('Test execution started');
      handleRunStarted(runId);
    } catch (err: any) {
      showError(err?.message || 'Failed to start test');
    }
  };

  /**
   * History → Save as configuration: a new configuration from a completed
   * run's snapshot (the run is not changed). Only once it exists, switch to
   * Configurations with it on top and marked New; on failure stay here.
   */
  const handleSaveRunAsConfig = async (run: TestRun) => {
    if (!activeOrganization || !currentUser || savingRunId) return;
    setSavingRunId(run.id);
    try {
      const created = await saveRunAsConfiguration(activeOrganization.id, currentUser.uid, run);
      setConfigurations((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
      setNewConfigId(created.id);
      changeTab('configurations');
      success(`Saved as configuration "${created.name}"`);
    } catch (err: any) {
      showError(err?.message || 'Failed to save the test as a configuration');
    } finally {
      setSavingRunId(null);
    }
  };

  const handleRetryRun = async (run: TestRun) => {
    // Re-run from the SNAPSHOT stored on the test_run, not from the live
    // testConfigurationId (which may have been edited since). Snapshot-based
    // retry is the fix for BUG-E.
    await handleQuickRunConfig(run.configuration, undefined);
  };

  const isStuckRun = (run: TestRun): boolean => {
    if (run.status !== 'initializing') return false;
    if (run.executionId) return false;
    if (!run.createdAt) return false;
    return Date.now() - new Date(run.createdAt).getTime() > 5 * 60 * 1000;
  };

  const handleClearStuckRuns = async (stuckRuns: TestRun[]) => {
    if (stuckRuns.length === 0) return;
    let okCount = 0;
    let failCount = 0;
    for (const run of stuckRuns) {
      try {
        await deleteTestRun(run.id, activeOrganization!.id);
        okCount++;
      } catch (err) {
        failCount++;
      }
    }
    if (okCount > 0) success(`Cleared ${okCount} stuck run${okCount === 1 ? '' : 's'}`);
    if (failCount > 0) showError(`Failed to clear ${failCount} run${failCount === 1 ? '' : 's'}`);
  };

  const handleTogglePauseConfig = async (config: TestConfiguration) => {
    if (!activeOrganization) return;
    const pausing = !config.paused;
    try {
      await updateTestConfiguration(config.id, activeOrganization.id, { paused: pausing });
      success(pausing ? 'Schedule paused' : 'Schedule resumed — next run re-anchored to its cadence');
      const refreshed = await getTestConfigurations(activeOrganization.id);
      setConfigurations(refreshed);
    } catch (err: any) {
      showError(`Failed to ${pausing ? 'pause' : 'resume'} schedule: ${err?.message ?? err}`);
    }
  };

  const handleEditConfig = (config: TestConfiguration) => {
    // Open the same Run Test dialog in edit mode. The dialog handles the
    // updateTestConfiguration call internally and fires onEditSaved on success.
    setEditingConfig(config);
    setShowEditWizard(true);
  };

  const handleEditSaved = () => {
    setShowEditWizard(false);
    setEditingConfig(null);
    // Refetch configurations to pick up the edit
    if (!activeOrganization) return;
    getTestConfigurations(activeOrganization.id)
      .then(setConfigurations)
      .catch((err) => console.error('Failed to refresh configurations after edit:', err));
  };

  // Status configuration - must match ExecutionStatus type
  const statusConfig: Record<string, { icon: typeof Activity; color: string; label: string; variant: 'default' | 'warning' | 'success' | 'error' | 'secondary' }> = {
    running: { icon: Activity, color: 'text-accent-blue', label: 'Running', variant: 'default' },
    initializing: { icon: Clock, color: 'text-accent-orange', label: 'Initializing', variant: 'warning' },
    paused: { icon: Clock, color: 'text-accent-yellow', label: 'Paused', variant: 'warning' },
    completed: { icon: CheckCircle, color: 'text-accent-green', label: 'Completed', variant: 'success' },
    failed: { icon: XCircle, color: 'text-accent-red', label: 'Failed', variant: 'error' },
    cancelled: { icon: StopCircle, color: 'text-muted-foreground', label: 'Cancelled', variant: 'secondary' },
  };

  // No organization state. Checked before the loading spinner: nothing loads without an
  // org, so a super admin with no membership used to spin on "Loading tests..." forever.
  if (!activeOrganization) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <EmptyState
            icon={<Activity className="w-12 h-12" />}
            title="No Organization Selected"
            description="Please select an organization to view tests"
            action={<SelectOrganizationButton />}
          />
        </div>
      </AppShell>
    );
  }

  // Loading state
  if (loadingConfigs && configurations.length === 0) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto" />
            <p className="text-muted-foreground">Loading tests...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {esimMoveDialog}
      <div className="space-y-4">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">Tests</h1>
            <p className="text-body text-muted-foreground">
              Configure, schedule, and monitor test executions
            </p>
          </div>

          {/* Button labels what it actually does: opens a Sheet that creates +
              optionally saves a test run. The previous "Run Test" label was
              ambiguous because the same flow also creates configurations. */}
          <div className="flex items-center gap-2">
            {/* #283 — a connectivity run is a test, so it is one of the options inside New Run's
                Flow picker rather than a second button with a second noun beside it. */}
            <Button
              variant="default"
              icon={<PlayCircle className="w-4 h-4" />}
              onClick={() => setShowExecutionDialog(true)}
            >
              New Run
            </Button>
          </div>
        </div>

        {/* Stats strip — label + value on one line so the row stays ~44px
            tall and the tables below get the viewport height back. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          <Card padding="sm" className="cursor-pointer hover:bg-muted/50 flex items-center justify-between gap-2" onClick={() => changeTab('configurations')}>
            <span className="text-caption text-muted-foreground uppercase truncate">Configurations</span>
            <span className="text-h2 font-bold text-foreground">{stats.configurations}</span>
          </Card>
          <Card padding="sm" className="cursor-pointer hover:bg-muted/50 flex items-center justify-between gap-2" onClick={() => changeTab('upcoming')}>
            <span className="text-caption text-muted-foreground uppercase truncate">Upcoming</span>
            {/* stats.upcoming counts one-off scheduled runs; recurring
                schedules only exist as config projections, so add them here. */}
            <span className="text-h2 font-bold text-foreground">{stats.upcoming + activeRecurringConfigs.length}</span>
          </Card>
          <Card padding="sm" className="cursor-pointer hover:bg-muted/50 flex items-center justify-between gap-2" onClick={() => changeTab('running')}>
            <span className="text-caption text-muted-foreground uppercase truncate">Running</span>
            <span className="text-h2 font-bold text-accent-blue">{stats.running}</span>
          </Card>
          <Card padding="sm" className="cursor-pointer hover:bg-muted/50 flex items-center justify-between gap-2" onClick={() => changeTab('history')}>
            <span className="text-caption text-muted-foreground uppercase truncate">Completed (24h)</span>
            <span className="text-h2 font-bold text-accent-green">{stats.completed24h}</span>
          </Card>
          <Card padding="sm" className="cursor-pointer hover:bg-muted/50 flex items-center justify-between gap-2" onClick={() => changeTab('history')}>
            <span className="text-caption text-muted-foreground uppercase truncate">Failed (24h)</span>
            <span className="text-h2 font-bold text-accent-red">{stats.failed24h}</span>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => changeTab(v as TabValue)} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="configurations" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configurations</span>
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Upcoming</span>
            </TabsTrigger>
            <TabsTrigger value="running" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Running</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          </TabsList>

          {/* Configurations Tab */}
          <TabsContent value="configurations" className="mt-4">
            <ResponsiveTable
              data={configurations}
              density="compact"
              minWidth={1080}
              searchable
              searchPlaceholder="Search test configurations by name, flow, location, or ID"
              searchKeys={['id']}
              columns={[
                {
                  header: 'Name',
                  key: 'name',
                  sortable: true,
                  render: (_, config) => (
                    <div>
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        {config.name}
                        {config.id === newConfigId && <Badge variant="success">New</Badge>}
                      </div>
                      {config.description && (
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {config.description}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  header: 'Flow',
                  key: 'flowName',
                  render: (_, config) => (
                    <div>
                      {config.flowName ? (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground truncate max-w-[150px]">
                            {config.flowName}
                          </span>
                          {config.flowType && (
                            <Badge variant="outline" className="text-xs">
                              {config.flowType === 'primitive' ? 'Primitive' : 'Scenario'}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic text-sm">No flow linked</span>
                      )}
                    </div>
                  ),
                },
                {
                  header: 'Timing',
                  key: 'timing',
                  render: (_, config) =>
                    config.timing.type === 'recurring' ? (
                      // Enable/disable the schedule in place — a Switch, not a
                      // play-triangle button, so it can't be confused with the
                      // row's Run action.
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!config.paused}
                          onCheckedChange={() => handleTogglePauseConfig(config)}
                          onClick={(e) => e.stopPropagation()}
                          title={config.paused ? 'Resume schedule' : 'Pause schedule'}
                        />
                        {config.paused ? (
                          <Badge variant="warning" className="gap-1">
                            <Pause className="w-3 h-3" />
                            Paused
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Recurring</Badge>
                        )}
                      </div>
                    ) : (
                      <Badge variant="secondary">
                        {config.timing.type === 'now' ? 'Immediate' : 'Scheduled'}
                      </Badge>
                    ),
                },
                {
                  header: 'Locations',
                  key: 'locations',
                  render: (_, config) => (
                    <span className="text-muted-foreground">
                      {config.locationRequirements.length || 'Any'}
                    </span>
                  ),
                },
                {
                  header: 'eSIM',
                  key: 'esim',
                  render: (_, config) => (
                    <Badge variant="outline">
                      {config.esim.mode === 'none' ? 'None' : config.esim.mode === 'single' ? 'Single' : 'Batch'}
                    </Badge>
                  ),
                },
                {
                  header: 'Usage',
                  key: 'usageCount',
                  sortable: true,
                  render: (_, config) => (
                    <span className="text-muted-foreground">{config.usageCount}x</span>
                  ),
                },
                {
                  header: 'Last Used',
                  key: 'lastUsedAt',
                  sortable: true,
                  render: (_, config) => (
                    <span className="text-muted-foreground">
                      {config.lastUsedAt ? formatRelativeTime(config.lastUsedAt) : 'Never'}
                    </span>
                  ),
                },
                {
                  header: 'Created By',
                  key: 'createdBy',
                  sortable: true,
                  sortValue: (config) => userName(config.createdBy),
                  render: (_, config) => (
                    <span className="text-sm text-muted-foreground truncate max-w-[160px] block">
                      {userName(config.createdBy)}
                    </span>
                  ),
                },
                {
                  header: 'Actions',
                  key: 'actions',
                  render: (_, config) => (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<PlayCircle className="w-4 h-4" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Row play = one-click direct submit; no dialog.
                          // Use the configure dialog only when the user wants
                          // to customize (top-right "Run Test" button).
                          handleQuickRunConfig(config, config.id);
                        }}
                        title="Run test"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Edit className="w-4 h-4" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditConfig(config);
                        }}
                        title="Edit configuration"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Copy className="w-4 h-4" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateConfig(config);
                        }}
                        title="Duplicate"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4 text-status-error" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete({ kind: 'config', id: config.id, name: config.name });
                        }}
                        title="Delete"
                      />
                    </div>
                  ),
                },
              ] as Column<TestConfiguration>[]}
              keyExtractor={(config) => config.id}
              emptyMessage="No configurations yet. Create a test configuration to reuse settings across test runs."
              paginated
              defaultPageSize={10}
            />
          </TabsContent>

          {/* Upcoming Tab */}
          <TabsContent value="upcoming" className="mt-4">
            {loadingUpcoming ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ResponsiveTable
                data={upcomingRows}
                density="compact"
                minWidth={720}
                searchable
                searchPlaceholder="Search upcoming tests by flow, configuration, or ID"
                searchKeys={['id']}
                columns={[
                  {
                    header: 'Flow',
                    key: 'flowName',
                    sortable: true,
                    render: (_, row) => (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate">{row.flowName}</span>
                        {row.kind === 'recurring' ? (
                          <Badge
                            variant="outline"
                            className="gap-1 text-xs font-normal shrink-0"
                            title="Next occurrence of a recurring schedule"
                          >
                            <Repeat className="w-3 h-3" />
                            {row.cadence}
                          </Badge>
                        ) : (
                          row.run && <ScheduleBadge run={row.run} />
                        )}
                      </div>
                    ),
                  },
                  {
                    header: 'Scheduled For',
                    key: 'scheduledFor',
                    sortable: true,
                    render: (_, row) => (
                      <span className="text-muted-foreground">
                        {fmt.formatDateTime(row.scheduledFor)}
                      </span>
                    ),
                  },
                  {
                    header: 'Configuration',
                    key: 'configurationName',
                    render: (_, row) => (
                      <span className="text-muted-foreground">{row.configurationName}</span>
                    ),
                  },
                  {
                    header: 'Actions',
                    key: 'actions',
                    render: (_, row) => (
                      <div className="flex items-center gap-1">
                        {row.kind === 'run' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<StopCircle className="w-4 h-4" />}
                            title="Cancel scheduled run"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRun(row.run!.id);
                            }}
                          />
                        ) : (
                          <>
                            {/* Active schedule — switch off to pause. Same
                                control as the Configurations tab's Timing
                                column, deliberately not a play/pause icon. */}
                            <Switch
                              checked
                              onCheckedChange={() => handleTogglePauseConfig(row.config!)}
                              onClick={(e) => e.stopPropagation()}
                              title="Pause schedule"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Edit className="w-4 h-4" />}
                              title="Edit schedule"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditConfig(row.config!);
                              }}
                            />
                          </>
                        )}
                      </div>
                    ),
                  },
                ] as Column<UpcomingRow>[]}
                keyExtractor={(row) => row.id}
                emptyMessage="No upcoming tests. Schedule a one-off run or create a recurring schedule from New Run."
                paginated
                defaultPageSize={10}
              />
            )}
          </TabsContent>

          {/* Running Tab */}
          <TabsContent value="running" className="mt-4">
            {loadingRunning ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {(() => {
                  if (!canManageStuckRuns) return null;
                  const stuckRuns = runningRuns.filter(isStuckRun);
                  if (stuckRuns.length === 0) return null;
                  return (
                    <div className="mb-3 px-3 py-2 rounded border border-status-warning bg-status-warning-bg/30 flex items-center justify-between">
                      <div className="text-sm">
                        <span className="font-medium text-status-warning">
                          {stuckRuns.length} stuck run{stuckRuns.length === 1 ? '' : 's'}
                        </span>
                        <span className="text-muted-foreground ml-2">
                          (no execution started after 5 minutes — safe to clear)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingDelete({ kind: 'stuck', runs: stuckRuns })}
                        icon={<Trash2 className="w-4 h-4" />}
                      >
                        Clear all stuck
                      </Button>
                    </div>
                  );
                })()}
                <ResponsiveTable
                  data={runningRuns}
                  density="compact"
                  minWidth={980}
                  searchable
                  searchPlaceholder="Search running tests by flow, location, status, or ID"
                  searchKeys={['id', 'executionId']}
                  columns={[
                  {
                    header: 'Flow',
                    key: 'flowName',
                    sortable: true,
                    render: (_, run) => (
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate">{run.flowName}</span>
                        <ScheduleBadge run={run} />
                      </div>
                        {isStuckRun(run) && (
                          <Badge variant="warning" className="text-xs">Stuck</Badge>
                        )}
                      </div>
                    ),
                  },
                  {
                    header: 'Task ID',
                    key: 'id',
                    render: (_, run) => <TaskIdCell id={run.id} />,
                  },
                  {
                    header: 'Status',
                    key: 'status',
                    render: (_, run) => {
                      const config = statusConfig[run.status] || statusConfig.running;
                      const StatusIcon = config.icon;
                      // Queue-wait reason mirrored by the dispatcher — without
                      // it an undispatchable run reads as a bare "Initializing"
                      // for up to 30 minutes with no hint why.
                      const pendingDetail = (run as any).pendingDetail as string | undefined;
                      return (
                        <div className="space-y-0.5 min-w-0">
                          <div className="flex items-center gap-2">
                            <StatusIcon className={cn('w-4 h-4 animate-pulse', config.color)} />
                            <Badge variant={config.variant}>{config.label}</Badge>
                          </div>
                          {run.status === 'initializing' && pendingDetail && (
                            <div
                              className="text-xs text-status-warning truncate max-w-[280px]"
                              title={pendingDetail}
                            >
                              {pendingDetail}
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    header: 'Location',
                    key: 'location',
                    render: (_, run) => {
                      const alloc = (run as any).allocation;
                      const locId = alloc?.locationId
                        ?? run.configuration.locationRequirements?.[0]?.locationId;
                      return (
                        <span className="text-sm text-muted-foreground">
                          {locId ? (locationNameById[locId] || locId) : '—'}
                        </span>
                      );
                    },
                  },
                  {
                    header: 'Modem',
                    key: 'modem',
                    render: (_, run) => {
                      const alloc = (run as any).allocation;
                      const imei = alloc?.modemImei as string | undefined;
                      const dev = alloc?.deviceId as string | undefined;
                      if (!imei && !dev) return <span className="text-muted-foreground text-xs">—</span>;
                      return (
                        <div className="text-xs space-y-0.5">
                          {imei && <div className="font-mono">…{imei.slice(-7)}</div>}
                          {dev && <div className="text-muted-foreground truncate max-w-[160px]">{dev}</div>}
                        </div>
                      );
                    },
                  },
                  {
                    header: 'Run By',
                    key: 'createdBy',
                    sortable: true,
                    sortValue: (run) => userName(run.createdBy),
                    render: (_, run) => (
                      <span className="text-sm text-muted-foreground truncate max-w-[160px] block">
                        {userName(run.createdBy)}
                      </span>
                    ),
                  },
                  {
                    // Same cell in Running and History so a run keeps its
                    // Started At when it moves tabs. Queued runs and runs
                    // cancelled before dispatch have no startedAt.
                    header: 'Started At',
                    key: 'startedAt',
                    sortable: true,
                    sortValue: (run) => (run.startedAt ? new Date(run.startedAt).getTime() : null),
                    render: (_, run) => renderStartedAt(run),
                  },
                  {
                    header: 'Actions',
                    key: 'actions',
                    render: (_, run) => (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Eye className="w-4 h-4" />}
                          title="View logs"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRun(run);
                          }}
                        />
                        {isStuckRun(run) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4 text-status-error" />}
                            title="Delete stuck run"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDelete({ kind: 'run', id: run.id, name: run.flowName });
                            }}
                          />
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<StopCircle className="w-4 h-4 text-status-error" />}
                            title="Cancel run"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRun(run.id);
                            }}
                          />
                        )}
                      </div>
                    ),
                  },
                ] as Column<TestRun>[]}
                  keyExtractor={(run) => run.id}
                  emptyMessage="No tests running. Start a test to see it running here."
                  paginated
                  defaultPageSize={10}
                />
              </>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ResponsiveTable
                data={historyRows}
                density="compact"
                minWidth={980}
                searchable
                searchPlaceholder="Search history by configuration, flow, location, status, or ID"
                searchKeys={['id', 'executionId', 'configurationName']}
                columns={[
                  {
                    header: 'Flow',
                    key: 'flowName',
                    sortable: true,
                    // Configuration rides as a subline instead of its own
                    // column — it's context, not a scan target, and the
                    // separate column was a major driver of the 8-column
                    // squeeze that forced zooming out.
                    render: (_, run) => (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground truncate">{run.flowName}</span>
                          <ScheduleBadge run={run} />
                        </div>
                        {run.configuration?.name && (
                          <div className="text-xs text-muted-foreground truncate">
                            {run.configuration.name}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    header: 'Task ID',
                    key: 'id',
                    render: (_, run) => <TaskIdCell id={run.id} />,
                  },
                  {
                    header: 'Status',
                    key: 'status',
                    sortable: true,
                    render: (_, run) => {
                      const config = statusConfig[run.status] || statusConfig.completed;
                      const StatusIcon = config.icon;
                      // Failure reason inline — a bare red "Failed" chip forces
                      // a click into the modal just to learn why.
                      const failureError = run.status === 'failed' ? run.result?.error : undefined;
                      return (
                        <div className="space-y-0.5 min-w-0">
                          <div className="flex items-center gap-2">
                            <StatusIcon className={cn('w-4 h-4', config.color)} />
                            <Badge variant={config.variant}>{config.label}</Badge>
                          </div>
                          {failureError && (
                            <div
                              className="text-xs text-muted-foreground truncate max-w-[280px]"
                              title={failureError}
                            >
                              {failureError}
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    header: 'Location',
                    key: 'location',
                    render: (_, run) => {
                      const alloc = (run as any).allocation;
                      const locId = alloc?.locationId
                        ?? run.configuration.locationRequirements?.[0]?.locationId;
                      return (
                        <span className="text-sm text-muted-foreground">
                          {locId ? (locationNameById[locId] || locId) : '—'}
                        </span>
                      );
                    },
                  },
                  {
                    header: 'Run By',
                    key: 'createdBy',
                    sortable: true,
                    sortValue: (run) => userName(run.createdBy),
                    render: (_, run) => (
                      <span className="text-sm text-muted-foreground truncate max-w-[160px] block">
                        {userName(run.createdBy)}
                      </span>
                    ),
                  },
                  {
                    // Same cell in Running and History so a run keeps its
                    // Started At when it moves tabs. Queued runs and runs
                    // cancelled before dispatch have no startedAt.
                    header: 'Started At',
                    key: 'startedAt',
                    sortable: true,
                    sortValue: (run) => (run.startedAt ? new Date(run.startedAt).getTime() : null),
                    render: (_, run) => renderStartedAt(run),
                  },
                  {
                    header: 'Duration',
                    key: 'duration',
                    render: (_, run) => (
                      <span className="text-muted-foreground">
                        {run.result?.duration ? formatDuration(run.result.duration) : '-'}
                      </span>
                    ),
                  },
                  {
                    header: 'Actions',
                    key: 'actions',
                    render: (_, run) => (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Eye className="w-4 h-4" />}
                          title="View logs"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRun(run);
                          }}
                        />
                        {run.status === 'failed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<RotateCw className="w-4 h-4" />}
                            title="Retry run"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRetryRun(run);
                            }}
                          />
                        )}
                        {canSaveAsConfiguration(run) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<BookmarkPlus className="w-4 h-4" />}
                            title="Save as configuration"
                            disabled={savingRunId === run.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveRunAsConfig(run);
                            }}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4 text-status-error" />}
                          title="Delete run"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete({ kind: 'run', id: run.id, name: run.flowName });
                          }}
                        />
                      </div>
                    ),
                  },
                ] as Column<TestRun>[]}
                keyExtractor={(run) => run.id}
                emptyMessage="No test history. Completed tests will appear here."
                paginated
                defaultPageSize={10}
                mobileCard={(run) => {
                  const config = statusConfig[run.status] || statusConfig.completed;
                  const StatusIcon = config.icon;
                  return (
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 flex items-center gap-2">
                          <span className="truncate font-medium text-foreground">{run.flowName}</span>
                          <ScheduleBadge run={run} />
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <StatusIcon className={cn('w-4 h-4', config.color)} />
                          <Badge variant={config.variant}>{config.label}</Badge>
                        </div>
                      </div>
                      {run.configuration?.name && (
                        <div className="truncate text-xs text-muted-foreground">
                          {run.configuration.name}
                        </div>
                      )}
                      <TaskIdCell id={run.id} />
                      {run.status === 'failed' && run.result?.error && (
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {run.result.error}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{fmt.formatDateTime(run.completedAt)}</span>
                        <span>{run.result?.duration ? formatDuration(run.result.duration) : ''}</span>
                      </div>
                      {(() => {
                        const alloc = (run as any).allocation;
                        const locId = alloc?.locationId
                          ?? run.configuration.locationRequirements?.[0]?.locationId;
                        if (!locId) return null;
                        return (
                          <div className="text-xs text-muted-foreground">
                            {locationNameById[locId] || locId}
                          </div>
                        );
                      })()}
                      <div className="flex justify-end gap-1 border-t border-border pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Eye className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRun(run);
                          }}
                        >
                          Logs
                        </Button>
                        {canSaveAsConfiguration(run) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<BookmarkPlus className="w-4 h-4" />}
                            title="Save as configuration"
                            disabled={savingRunId === run.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveRunAsConfig(run);
                            }}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4 text-status-error" />}
                          title="Delete run"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete({ kind: 'run', id: run.id, name: run.flowName });
                          }}
                        />
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Execution Dialog */}
      <ExecutionDialog
        isOpen={showExecutionDialog}
        onClose={() => {
          setShowExecutionDialog(false);
          setSelectedConfig(null);
        }}
        preselectedConfig={selectedConfig}
        onRunStarted={handleRunStarted}
        onConnectivityStarted={(runId) => setCampaignRunId(runId)}
      />

      {/* #283 — connectivity run detail, scoped to the run rather than a device. */}
      {campaignRunId && (
        <Sheet open={!!campaignRunId} onOpenChange={(o) => !o && setCampaignRunId(null)}>
          <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Connectivity run</SheetTitle>
              <SheetDescription>Live state, events and the tests it fired.</SheetDescription>
            </SheetHeader>
            <div className="py-4">
              <CampaignRunDetail runId={campaignRunId} />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Edit Configuration — reuses the same Run Test Sheet in edit mode. */}
      {editingConfig && (
        <ExecutionDialog
          isOpen={showEditWizard}
          onClose={() => {
            setShowEditWizard(false);
            setEditingConfig(null);
          }}
          editingConfig={editingConfig}
          onEditSaved={handleEditSaved}
        />
      )}

      {/* Logs Modal */}
      {selectedRun && showLogsModal && (
        <Modal
          isOpen={showLogsModal}
          onClose={() => {
            setShowLogsModal(false);
            setSelectedRun(null);
          }}
          title="Test Run Details"
          size="xl"
          bodyClassName="min-h-0 flex-1 flex flex-col overflow-hidden pb-4"
        >
          {/* Fixed header + tabbed body so only the active tab scrolls — no
              nested "double scroll". The header (flow + status + metadata)
              stays put; Overview and Logs each own their single scroll region.
              Body is a flex column (overflow-hidden) so this flex chain bounds
              each tab's scroll region instead of the modal body scrolling. */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Fixed header */}
            <div className="flex-shrink-0 space-y-2 border-b border-border pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-medium text-foreground">{quickTestName(selectedRun.flowId) || selectedRun.flowName}</h3>
                  <p className="truncate text-xs text-muted-foreground">ID: {selectedRun.id}</p>
                </div>
                {statusConfig[selectedRun.status] && (
                  <Badge variant={statusConfig[selectedRun.status].variant} className="flex-shrink-0">
                    {statusConfig[selectedRun.status].label}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Started {fmt.formatDateTime(selectedRun.startedAt)}</span>
                <span>
                  · Duration{' '}
                  {(() => {
                    // The whole run, including setup before the flow (eSIM move).
                    const finished = ['completed', 'failed', 'cancelled'].includes(selectedRun.status);
                    const ms = finished
                      ? wholeRunMs({
                          durationMs: selectedRun.result?.duration,
                          startedAt: selectedRun.startedAt ?? null,
                          completedAt: selectedRun.completedAt ?? null,
                          firstStepAt: firstStepTime(selectedRunSetupSteps),
                        })
                      : undefined;
                    return ms ? formatDuration(ms) : 'in progress…';
                  })()}
                </span>
              </div>
              <TestRunSetup
                run={selectedRun}
                locationName={(() => {
                  const locId = (selectedRun as any).allocation?.locationId
                    ?? selectedRun.configuration.locationRequirements?.[0]?.locationId;
                  return locId ? (locationNameById[locId] || locId) : undefined;
                })()}
              />
            </div>

            <Tabs
              value={detailsTab}
              onValueChange={(v) => setDetailsTab(v as 'overview' | 'logs' | 'signaling' | 'artifacts')}
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList className="mt-3 flex-shrink-0 self-start">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
                {(selectedRunDiag || selectedRunArtifacts.some((a) => a.type === 'l3_trace')) && (
                  <TabsTrigger value="signaling">Signaling</TabsTrigger>
                )}
                {(selectedRunArtifacts.length > 0 ||
                  visibleArtifactSkips(selectedRunArtifactSkips, selectedRunArtifacts.map((a) => a.type)).length > 0) && (
                  <TabsTrigger value="artifacts">Artifacts</TabsTrigger>
                )}
              </TabsList>

              {/* Overview — single scroll region */}
              <TabsContent value="overview" className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="space-y-4">
                  {/* Glanceable live progress — see motion without reading logs. */}
                  {selectedRun.executionId && (
                    <ExecutionProgress
                      status={selectedRunStatus ?? selectedRun.status}
                      isLive={isLive}
                      startedAt={selectedRun.startedAt ?? null}
                      completedAt={selectedRun.completedAt ?? null}
                      durationMs={selectedRun.result?.duration}
                      logs={logs}
                      currentNode={selectedRunCurrentNode}
                      error={selectedRun.result?.error}
                      pendingReason={selectedRunPending.reason}
                      pendingDetail={selectedRunPending.detail}
                      lastDispatchAttemptAt={selectedRunPending.lastDispatchAttemptAt ?? null}
                      attemptCount={selectedRunPending.attemptCount}
                      setupSteps={selectedRunSetupSteps}
                    />
                  )}

                  {selectedRun?.flowId === 'sys_quick_speedtest_test_flow' && (() => {
                    const speedResult = (selectedRun.result?.outputs as any)?.speedResult;
                    return (
                      <SpeedTestGauges
                        liveSample={selectedRunConnectivity.liveSample}
                        downloadMbps={speedResult?.downloadMbps}
                        uploadMbps={speedResult?.uploadMbps}
                        pingMs={speedResult?.pingMs}
                        jitterMs={speedResult?.jitterMs}
                        isLive={isLive}
                        peakDownloadMbps={speedResult?.peakDownloadMbps}
                        peakUploadMbps={speedResult?.peakUploadMbps}
                        steadyDownloadMbps={speedResult?.steadyDownloadMbps}
                        steadyUploadMbps={speedResult?.steadyUploadMbps}
                        packetLossPct={speedResult?.packetLossPct}
                        idleLatencyMs={speedResult?.idleLatencyMs}
                        downloadLatencyMs={speedResult?.downloadLatencyMs}
                        uploadLatencyMs={speedResult?.uploadLatencyMs}
                        bufferbloatMs={speedResult?.bufferbloatMs}
                        downloadSamples={speedResult?.downloadSamples}
                        uploadSamples={speedResult?.uploadSamples}
                      />
                    );
                  })()}

                  {selectedRun?.flowId === 'sys_quick_voice_call_test_flow' && (() => {
                    const outputs = (selectedRun.result?.outputs as any) || {};
                    const call = outputs.callResult || {};
                    const ims = outputs.imsResult || {};
                    const hasAny = call.bearer || typeof call.setupMs === 'number' || call.aNumber || call.bNumber || call.status;
                    if (!hasAny) return null;

                    const codecLabel = (c: any) => c === 'nb' ? 'AMR-NB' : c === 'wb' ? 'AMR-WB' : c ? String(c) : null;
                    const fmtMs = (v: any) => typeof v === 'number' ? (v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`) : null;
                    const aNum = call.aNumber || null;
                    const bNum = call.bNumber || null;

                    const chips: Array<{ label: string; value: React.ReactNode }> = [];
                    if (call.bearer) chips.push({ label: 'Bearer', value: String(call.bearer) });
                    const codec = codecLabel(call.codec);
                    if (codec) chips.push({ label: 'Codec', value: codec });
                    if (typeof call.setupMs === 'number') chips.push({ label: 'Setup time', value: fmtMs(call.setupMs) });
                    if (typeof call.callDurationMs === 'number' && call.callDurationMs > 0) chips.push({ label: 'Talk time', value: fmtMs(call.callDurationMs) });
                    if (typeof call.ringMs === 'number' && call.status !== 'success') chips.push({ label: 'Ring time', value: fmtMs(call.ringMs) });
                    if (typeof call.dropped === 'boolean') chips.push({ label: 'Dropped', value: call.dropped ? 'Yes' : 'No' });
                    const imsVal = ims.wouldSupport ?? (typeof call.imsRegistered === 'boolean' ? (call.imsRegistered ? 'registered' : 'not registered') : null);
                    if (imsVal) chips.push({ label: 'IMS', value: String(imsVal) });
                    if (typeof call.rsrp === 'number') chips.push({ label: 'RSRP', value: `${call.rsrp} dBm` });
                    if (typeof call.rsrq === 'number') chips.push({ label: 'RSRQ', value: `${call.rsrq} dB` });
                    if (typeof call.sinr === 'number') chips.push({ label: 'SINR', value: `${call.sinr} dB` });

                    // Pass/fail mirrors the AI voice agent: a connected call
                    // (even if the far end hung up early) is a working call.
                    // New runs carry `call.passed`; older ones fall back to the
                    // edge's raw success flag.
                    const callPassed = call.passed === true || (call.passed == null && call.status === 'success');

                    return (
                      <div className="rounded border border-border bg-card p-4">
                        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                          <Phone className="h-4 w-4 text-primary" />
                          Voice Call Result
                          <Badge variant={callPassed ? 'success' : 'error'} className="ml-1">
                            {callPassed ? 'Passed' : 'Failed'}
                          </Badge>
                          {call.outcomeLabel && (
                            <span className={cn(
                              'ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              callPassed
                                ? 'bg-status-success-bg text-status-success'
                                : 'bg-amber-500/15 text-amber-600 dark:text-amber-500',
                            )}>
                              {String(call.outcomeLabel)}
                            </span>
                          )}
                        </div>
                        {(aNum || bNum) && (
                          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-mono text-foreground">{aNum || 'unknown'}</span>
                            <span className="text-xs text-muted-foreground">(A · caller)</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-mono text-foreground">{bNum || '—'}</span>
                            <span className="text-xs text-muted-foreground">(B · called)</span>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          {chips.map(c => (
                            <div key={c.label} className="rounded bg-muted p-2">
                              <div className="text-xs text-muted-foreground">{c.label}</div>
                              <div className="text-sm font-medium text-foreground">{c.value}</div>
                            </div>
                          ))}
                        </div>
                        {/* Human explanation of the outcome (why it passed with
                            a note, or why it failed). */}
                        {call.outcomeReason ? (
                          <div className="mt-3 flex items-start gap-1.5 text-sm text-muted-foreground">
                            {!callPassed && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />}
                            <span>{String(call.outcomeReason)}</span>
                          </div>
                        ) : (!callPassed && call.finalState) ? (
                          <div className="mt-2 text-xs text-muted-foreground">
                            Final state: {String(call.finalState)}
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}

                  {selectedRun.result?.error && (
                    <div className="rounded bg-status-error-bg p-3 text-status-error">
                      <div className="mb-1 text-sm font-medium">Error</div>
                      <div className="text-sm">{selectedRun.result.error}</div>
                    </div>
                  )}

                  {/* AI voice-agent call transcript (voiceAgentTest runs). Reads
                      the agentResult from result.outputs; shows on success AND
                      failure. Secrets already redacted upstream ([DTMF ***]). */}
                  {(() => {
                    const outs = (selectedRun.result?.outputs as any) || {};
                    let ar: any = null;
                    for (const v of Object.values(outs)) {
                      if (v && typeof v === 'object' && Array.isArray((v as any).transcript) && (v as any).transcript.length) { ar = v; break; }
                    }
                    if (!ar) return null;
                    const turns: Array<{ role?: string; text?: string; t?: number }> = ar.transcript;
                    const fmtT = (t?: number) => typeof t === 'number' ? `+${t}s` : '';
                    return (
                      <div className="rounded border border-border bg-card p-4">
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                          <Phone className="h-4 w-4 text-primary" />
                          Call Transcript
                          <span className="text-xs font-normal text-muted-foreground">
                            {turns.length} turns
                            {typeof ar.connectedSec === 'number' ? ` · held ${ar.connectedSec}s` : ''}
                            {ar.dtmfSent ? ` · DTMF sent ${ar.dtmfSent}` : ''}
                            {ar.dtmfDetected ? ` · DTMF heard ${ar.dtmfDetected}` : ''}
                          </span>
                          {/* External outcome (busy / no answer / rejected) — the
                              call reached the far end but it didn't connect. */}
                          {ar.outcomeLabel && ar.passed === false ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-500">
                              <AlertTriangle className="h-3 w-3" />
                              {ar.outcomeLabel}
                            </span>
                          ) : null}
                        </div>
                        <div className="max-h-[320px] space-y-2 overflow-y-auto">
                          {turns.map((t, i) => {
                            const role = (t.role || '').toLowerCase();
                            const isAgent = role === 'agent';
                            const isSystem = role === 'system';
                            if (isSystem) return (
                              <div key={i} className="px-4 py-1 text-center text-xs italic text-muted-foreground">
                                {fmtT(t.t) && <span className="font-mono not-italic">{fmtT(t.t)} </span>}{t.text}
                              </div>
                            );
                            return (
                              <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[78%] rounded-lg px-3 py-1.5 text-sm ${isAgent ? 'bg-primary/15 text-foreground' : 'border border-border bg-background text-foreground'}`}>
                                  <div className="mb-0.5 flex items-center justify-between gap-3 text-[10px] uppercase tracking-wide text-muted-foreground">
                                    <span>{isAgent ? 'AI agent' : 'Far side'}</span>
                                    {fmtT(t.t) && <span className="font-mono normal-case">{fmtT(t.t)}</span>}
                                  </div>
                                  {t.text}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* "What kind of connectivity did I get?" — country / location /
                      operator / RAT / signal / IP. */}
                  <ConnectivitySnapshot
                    metrics={selectedRunConnectivity.metrics}
                    bearerClass={selectedRunConnectivity.bearerClass}
                    registeredNetwork={selectedRunConnectivity.registeredNetwork}
                    registeredNetworkName={selectedRunConnectivity.registeredNetworkName}
                    registeredNetworkType={selectedRunConnectivity.registeredNetworkType}
                    registeredNetworkMcc={selectedRunConnectivity.registeredNetworkMcc}
                    registeredNetworkMnc={selectedRunConnectivity.registeredNetworkMnc}
                    locationName={selectedRunConnectivity.locationName}
                    countryCode={selectedRunConnectivity.countryCode}
                  />

                  {selectedRun.result?.summary && (
                    <div className="rounded bg-muted p-3">
                      <div className="mb-2 text-sm font-medium text-foreground">Summary</div>
                      <SummaryView text={selectedRun.result.summary} />
                    </div>
                  )}

                  {(selectedRun.result as any)?.resultText && (
                    <div className="rounded bg-muted p-3">
                      <div className="mb-1 text-sm font-medium text-foreground">Results</div>
                      <pre className="whitespace-pre-wrap font-mono text-sm text-muted-foreground">{(selectedRun.result as any).resultText}</pre>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Logs — the viewer fills the panel height; single scroll region */}
              <TabsContent value="logs" className="mt-3 flex min-h-0 flex-1 flex-col">
                {selectedRun.executionId ? (
                  logsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-muted-foreground">Loading logs...</span>
                    </div>
                  ) : (
                    <ExecutionLogViewer
                      logs={logs}
                      isLive={isLive}
                      autoScroll={true}
                      showFilters={true}
                      showExport={true}
                      fillHeight
                      className="min-h-0 flex-1"
                    />
                  )
                ) : (
                  <div className="rounded bg-muted/50 py-8 text-center text-muted-foreground">
                    No execution logs available. The test may not have started yet.
                  </div>
                )}
              </TabsContent>

              {/* Signaling — decoded DIAG (reject cause + RF measurements) */}
              <TabsContent value="signaling" className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                <NetworkDiagnosticsPanel
                  diag={selectedRunDiag}
                  artifacts={selectedRunArtifacts}
                  onDownload={handleArtifactDownload}
                  compact
                />
              </TabsContent>

              {/* Artifacts — every artifact for the run (pcap / l3_trace / at_log / voice_wav / …).
                  voice_wav rows play inline; see ExecutionArtifacts. */}
              <TabsContent value="artifacts" className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                {selectedRunArtifacts.length > 0 && (
                  <ExecutionArtifacts
                    artifacts={selectedRunArtifacts}
                    executionId={selectedRun?.executionId ?? ''}
                    organizationId={activeOrganization?.id}
                  />
                )}
                {(() => {
                  const skips = visibleArtifactSkips(selectedRunArtifactSkips, selectedRunArtifacts.map((a) => a.type));
                  if (skips.length === 0) return null;
                  return (
                    <div className={selectedRunArtifacts.length > 0 ? 'mt-4' : ''}>
                      <div className="text-caption text-muted-foreground uppercase mb-2">
                        Captures not available
                      </div>
                      <ul className="space-y-1.5">
                        {skips.map(([kind, code]) => (
                          <li key={kind} className="text-body-sm text-muted-foreground">
                            <span className="text-foreground font-medium">{artifactKindLabel(kind)}</span>
                            {' — '}{artifactSkipReason(code)}.
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </div>
        </Modal>
      )}
      <DeleteConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        itemName={pendingDelete && pendingDelete.kind !== 'stuck' ? pendingDelete.name : undefined}
        message={
          pendingDelete?.kind === 'stuck'
            ? `Are you sure you want to delete ${pendingDelete.runs.length} stuck run${pendingDelete.runs.length === 1 ? '' : 's'}?`
            : undefined
        }
        details={
          pendingDelete?.kind === 'stuck'
            ? "They've been initializing for more than 5 minutes with no execution attached."
            : undefined
        }
        onConfirm={async () => {
          if (!pendingDelete) return;
          if (pendingDelete.kind === 'config') await handleDeleteConfig(pendingDelete.id);
          else if (pendingDelete.kind === 'run') await handleDeleteRun(pendingDelete.id);
          else await handleClearStuckRuns(pendingDelete.runs);
        }}
      />
    </AppShell>
  );
}
